import type { AccountSettings, DeviceSettings } from '@/api/queries/settings';

/**
 * Setting-kind routing.
 *
 * Every stored setting key maps to exactly one kind, which picks its backing
 * store: `account` (server document, React Query + network) or `device` (local
 * storage document). This is the minimal bridge Phase 3's full settings registry
 * will build on — keep it a plain, typed key -> kind map.
 *
 * `schemaVersion` is document metadata, not a user-settable key, so it is
 * excluded from both key unions.
 */
export type SettingKind = 'account' | 'device';

export type AccountSettingKey = Exclude<keyof AccountSettings, 'schemaVersion'>;
export type DeviceSettingKey = Exclude<keyof DeviceSettings, 'schemaVersion'>;
export type SettingKey = AccountSettingKey | DeviceSettingKey;

export const SETTING_KINDS: Record<SettingKey, SettingKind> = {
  // Account settings — stored on the server, follow the user across devices.
  units: 'account',
  defaultLandingScreen: 'account',
  pushEnabled: 'account',
  emailEnabled: 'account',
  friendActivity: 'account',
  newFollowers: 'account',
  recommendations: 'account',
  reviewReminders: 'account',
  // Device settings — stored per-install in local storage.
  reduceMotion: 'device',
};

/** Narrow a key to its kind. */
export function settingKind(key: SettingKey): SettingKind {
  return SETTING_KINDS[key];
}
